<template>
  <div class="chat-page container-fluid py-3">
    <div class="chat-panel card shadow-sm">
      <div class="chat-header card-header pb-2">
        <div class="d-flex align-items-center">
          <i class="fas fa-robot text-primary me-2"></i>
          <div>
            <h6 class="mb-0">Chat AI</h6>
            <p class="text-xs text-muted mb-0">Asistente de chat con inteligencia artificial</p>
          </div>
        </div>
      </div>

      <div ref="messagesContainer" class="chat-messages">
        <div v-if="messages.length === 0" class="chat-empty">
          <i class="fas fa-comments chat-empty-icon"></i>
          <h5>¿En qué puedo ayudarte?</h5>
          <p class="text-muted mb-0">Escribe tu pregunta en el campo de abajo para comenzar.</p>
        </div>

        <div
          v-for="(message, index) in messages"
          :key="index"
          class="chat-message"
          :class="message.role === 'user' ? 'chat-message-user' : 'chat-message-assistant'"
        >
          <div class="chat-avatar">
            <i :class="message.role === 'user' ? 'fas fa-user' : 'fas fa-robot'"></i>
          </div>
          <div class="chat-bubble" :class="{ 'chat-bubble-chart': message.chart }">
            <div
              v-if="isLoading && message.role === 'assistant' && !message.content"
              class="chat-typing"
            >
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div
              v-else-if="message.role === 'assistant'"
              class="chat-text chat-text-rich mb-0"
              v-html="renderMarkdown(message.content)"
            ></div>
            <chat-chart
              v-if="message.role === 'assistant' && message.chart"
              :config="message.chart"
            />
            <p v-else-if="message.role === 'user'" class="chat-text mb-0">{{ message.content }}</p>
            <span v-if="message.model" class="chat-model">{{ message.model }}</span>
          </div>
        </div>

        <div v-if="errorText" class="chat-error">{{ errorText }}</div>
      </div>

      <div class="chat-footer">
        <div class="chat-input-wrapper">
          <textarea
            ref="questionInput"
            v-model="question"
            class="form-control chat-input"
            rows="1"
            placeholder="Escribe tu pregunta..."
            :disabled="isLoading"
            @keydown.enter.exact.prevent="sendMessage"
          ></textarea>
          <argon-button
            color="primary"
            size="sm"
            class="chat-send-btn"
            :disabled="!question.trim() || isLoading"
            @click="sendMessage"
          >
            <i class="fas fa-paper-plane"></i>
          </argon-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import ChatChart from "@/views/admin/chat/ChatChart.vue";
import { renderChatMarkdown } from "@/utils/chatMarkdown.js";

export default {
  name: "ChatAI",
  components: {
    ArgonButton,
    ChatChart,
  },
  data() {
    return {
      question: "",
      isLoading: false,
      errorText: "",
      messages: [],
    };
  },
  mounted() {
    this.$store.state.showFooter = false;
    this.$nextTick(() => {
      this.$refs.questionInput?.focus();
    });
  },
  beforeUnmount() {
    this.$store.state.showFooter = true;
  },
  methods: {
    renderMarkdown(content) {
      return renderChatMarkdown(content);
    },
    async sendMessage() {
      const text = this.question.trim();
      if (!text || this.isLoading) return;

      const userMessage = { role: "user", content: text };
      this.messages.push(userMessage);
      this.question = "";
      this.isLoading = true;
      this.errorText = "";
      this.scrollToBottom();

      const assistantIndex = this.messages.length;
      this.messages.push({ role: "assistant", content: "", model: "" });

      try {
        const history = this.messages
          .slice(0, assistantIndex - 1)
          .filter((msg) => msg.role === "user" || msg.role === "assistant")
          .slice(-12)
          .map((msg) => ({ role: msg.role, content: msg.content }));

        const response = await this.$store.dispatch("chat/send", {
          message: userMessage.content,
          history,
        });

        const answer = response?.data?.answer || response?.data?.reply || "";
        const meta = response?.data?.meta || {};

        this.messages[assistantIndex] = {
          role: "assistant",
          content: answer || "No se recibió respuesta del asistente.",
          chart: response?.data?.chart || null,
          model: meta.modelLabel || meta.model || "",
        };
      } catch (error) {
        console.error("Error al enviar mensaje:", error);
        this.messages.splice(assistantIndex, 1);
        this.errorText =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Ocurrió un error al procesar tu pregunta. Intenta de nuevo.";
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
          this.$refs.questionInput?.focus();
        });
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
  },
};
</script>

<style scoped>
.chat-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 5.5rem);
  min-height: 480px;
  padding-bottom: 0.75rem !important;
}

.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: 0.75rem;
}

.chat-header {
  flex-shrink: 0;
  border-bottom: 1px solid #e9ecef;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: #f8f9fa;
}

.chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  text-align: center;
  color: #67748e;
}

.chat-empty-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}

.chat-message {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  max-width: 820px;
}

.chat-message-assistant:has(.chat-bubble-chart) {
  max-width: 560px;
  width: 100%;
}

.chat-message-user {
  margin-left: auto;
  flex-direction: row-reverse;
}

.chat-message-assistant {
  margin-right: auto;
}

.chat-avatar {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.chat-message-user .chat-avatar {
  background: #5e72e4;
  color: #fff;
}

.chat-message-assistant .chat-avatar {
  background: #fff;
  color: #5e72e4;
  border: 1px solid #dee2e6;
}

.chat-bubble {
  padding: 0.5rem 0.75rem;
  border-radius: 0.625rem;
  max-width: calc(100% - 36px);
}

.chat-message-user .chat-bubble {
  background: #5e72e4;
  color: #fff;
  border-bottom-right-radius: 0.25rem;
}

.chat-message-assistant .chat-bubble {
  background: #fff;
  color: #344767;
  border: 1px solid #e9ecef;
  border-bottom-left-radius: 0.25rem;
}

.chat-bubble-chart {
  min-width: min(100%, 520px);
}

.chat-text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  font-size: 0.875rem;
}

.chat-text-rich {
  white-space: normal;
}

.chat-text-rich :deep(p) {
  margin: 0 0 0.5rem;
}

.chat-text-rich :deep(p:last-child) {
  margin-bottom: 0;
}

.chat-text-rich :deep(ul) {
  margin: 0 0 0.5rem;
  padding-left: 1.25rem;
}

.chat-text-rich :deep(li) {
  margin-bottom: 0.25rem;
}

.chat-text-rich :deep(strong) {
  font-weight: 700;
}

.chat-message-user .chat-text-rich :deep(strong) {
  font-weight: 700;
}

.chat-model {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.65rem;
  opacity: 0.6;
}

.chat-error {
  max-width: 820px;
  margin: 0 auto 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: #fdecea;
  color: #c0341d;
  font-size: 0.875rem;
}

.chat-typing {
  display: flex;
  gap: 4px;
  padding: 0.25rem 0;
}

.chat-typing span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #adb5bd;
  animation: chat-typing 1.2s infinite ease-in-out;
}

.chat-typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.chat-typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes chat-typing {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.chat-footer {
  flex-shrink: 0;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

.chat-input-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 0.75rem;
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
}

.chat-input {
  flex: 1;
  width: auto;
  resize: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  min-height: 44px;
  max-height: 120px;
  line-height: 1.5;
  border: 1px solid #dee2e6;
}

.chat-input:focus {
  border-color: #5e72e4;
  box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.15);
}

.chat-send-btn {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  min-width: 44px;
  padding: 0;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
